import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, PaperPlaneTilt, Clock, FileText, Warning, CheckCircle, Eye, Trash, Pause, Play } from "@phosphor-icons/react";
import { format } from "date-fns";

const TABS = [
    { k: "", label: "All", icon: null },
    { k: "draft", label: "Drafts", icon: FileText },
    { k: "scheduled", label: "Scheduled", icon: Clock },
    { k: "sending", label: "Sending", icon: PaperPlaneTilt },
    { k: "sent", label: "Sent", icon: CheckCircle },
    { k: "failed", label: "Failed", icon: Warning },
];

export default function AdminCampaigns() {
    const [tab, setTab] = useState("");
    const [items, setItems] = useState([]);

    const load = useCallback(async () => {
        const { data } = await api.get("/admin/campaigns", { params: tab ? { status: tab } : {} });
        setItems(data.items);
    }, [tab]);
    useEffect(() => { load(); }, [load]);
    useEffect(() => { document.title = "Surveys2CAD | Campaigns"; }, []);

    const remove = async (id) => {
        if (!window.confirm("Delete this campaign and all its events?")) return;
        await api.delete(`/admin/campaigns/${id}`);
        toast.success("Deleted");
        load();
    };

    const togglePause = async (c) => {
        const action = c.status === "paused" ? "resume" : "pause";
        try {
            const { data } = await api.post(`/admin/campaigns/${c.id}/${action}`);
            toast.success(action === "pause" ? "Paused — auto-fire skipped" : `Resumed → ${data.status}`);
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || `${action} failed`);
        }
    };

    const statusColor = (s) => ({
        draft: "bg-slate-700 text-slate-300",
        scheduled: "bg-blue-500/20 text-blue-300",
        sending: "bg-orange-500/20 text-orange-300",
        sent: "bg-emerald-500/20 text-emerald-300",
        failed: "bg-red-500/20 text-red-300",
        paused: "bg-slate-600 text-slate-200",
    }[s] || "bg-slate-700 text-slate-300");

    return (
        <div className="p-4 sm:p-6 lg:p-8" data-testid="admin-campaigns">
            <div className="flex items-end justify-between flex-wrap gap-3 mb-6">
                <div className="min-w-0">
                    <div className="overline mb-2">Campaigns</div>
                    <h1 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-50 tracking-tight">Email Campaigns</h1>
                    <p className="text-slate-400 mt-2 text-sm">Draft, schedule, send, and track all in one place.</p>
                </div>
                <Link to="/admin/campaigns/new" className="btn-primary" data-testid="new-campaign-btn">
                    <Plus size={16} weight="bold" /> New campaign
                </Link>
            </div>

            <div className="flex flex-wrap gap-2 mb-6 overflow-x-auto -mx-1 px-1 pb-1" data-testid="campaign-tabs">
                {TABS.map((t) => (
                    <button
                        key={t.k || "all"}
                        onClick={() => setTab(t.k)}
                        className={`px-3 py-1.5 rounded-full text-xs font-heading font-medium flex items-center gap-1.5 transition-colors whitespace-nowrap ${
                            tab === t.k
                                ? "bg-cyan-500/15 border border-cyan-400/50 text-cyan-200"
                                : "bg-slate-900/60 border border-slate-800 text-slate-400 hover:text-slate-200"
                        }`}
                        data-testid={`tab-${t.k || "all"}`}
                    >
                        {t.icon && <t.icon size={12} />} {t.label}
                    </button>
                ))}
            </div>

            {items.length === 0 ? (
                <div className="tech-card p-10 text-center">
                    <p className="text-slate-400 mb-4">No campaigns yet in this view.</p>
                    <Link to="/admin/campaigns/new" className="btn-primary inline-flex">
                        <Plus size={14} weight="bold" /> Create your first campaign
                    </Link>
                </div>
            ) : (
                <>
                    {/* --- Desktop table --- */}
                    <div className="tech-card overflow-hidden hidden lg:block">
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="border-b border-slate-800 bg-slate-900/40">
                                        {["Name", "Subject", "Status", "Recipients", "Sent", "Opens", "Clicks", "Bounces", "Created", ""].map((h) => (
                                            <th key={h} className="text-left px-4 py-3 overline whitespace-nowrap">{h}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {items.map((c) => {
                                        const s = c.stats || {};
                                        const openRate = s.sent ? ((s.opened || 0) / s.sent * 100).toFixed(1) : "—";
                                        return (
                                            <tr key={c.id} className="border-b border-slate-800/60 hover:bg-slate-900/30" data-testid={`campaign-${c.id}`}>
                                                <td className="px-4 py-3 text-slate-100 font-heading font-medium">{c.name}</td>
                                                <td className="px-4 py-3 text-slate-300 max-w-[280px] truncate">{c.subject}</td>
                                                <td className="px-4 py-3">
                                                    <span className={`px-2 py-0.5 rounded-full text-xs font-mono-alt ${statusColor(c.status)}`}>{c.status}</span>
                                                </td>
                                                <td className="px-4 py-3 text-slate-200 font-mono-alt text-xs">{s.total || 0}</td>
                                                <td className="px-4 py-3 text-slate-200 font-mono-alt text-xs">{s.sent || 0}</td>
                                                <td className="px-4 py-3 text-cyan-300 font-mono-alt text-xs">{s.opened || 0} {s.sent ? <span className="text-slate-500">({openRate}%)</span> : null}</td>
                                                <td className="px-4 py-3 text-orange-300 font-mono-alt text-xs">{s.clicked || 0}</td>
                                                <td className="px-4 py-3 text-red-300 font-mono-alt text-xs">{s.bounced || 0}</td>
                                                <td className="px-4 py-3 text-slate-400 font-mono-alt text-xs whitespace-nowrap">
                                                    {c.created_at ? format(new Date(c.created_at), "MMM d") : "—"}
                                                </td>
                                                <td className="px-4 py-3 text-right">
                                                    <div className="flex justify-end gap-1">
                                                        {(c.status === "scheduled" || c.status === "paused") && (
                                                            <button
                                                                onClick={() => togglePause(c)}
                                                                className={`p-1.5 ${c.status === "paused" ? "text-emerald-300 hover:text-emerald-200" : "text-amber-300 hover:text-amber-200"}`}
                                                                title={c.status === "paused" ? "Resume — re-enable auto-fire" : "Pause — auto-fire skipped"}
                                                                data-testid={`campaign-${c.status === "paused" ? "resume" : "pause"}-${c.id}`}
                                                            >
                                                                {c.status === "paused" ? <Play size={15} weight="fill" /> : <Pause size={15} weight="fill" />}
                                                            </button>
                                                        )}
                                                        <Link to={`/admin/campaigns/${c.id}`} className="p-1.5 text-slate-400 hover:text-cyan-300">
                                                            <Eye size={15} />
                                                        </Link>
                                                        <button onClick={() => remove(c.id)} className="p-1.5 text-slate-400 hover:text-red-400">
                                                            <Trash size={15} />
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {/* --- Mobile / tablet card list --- */}
                    <div className="lg:hidden space-y-3" data-testid="campaigns-cards">
                        {items.map((c) => {
                            const s = c.stats || {};
                            const openRate = s.sent ? ((s.opened || 0) / s.sent * 100).toFixed(1) : null;
                            return (
                                <div key={c.id} className="tech-card p-4" data-testid={`campaign-card-${c.id}`}>
                                    <div className="flex items-start justify-between gap-3 mb-2">
                                        <Link to={`/admin/campaigns/${c.id}`} className="min-w-0 flex-1">
                                            <div className="font-heading font-semibold text-slate-50 text-base truncate">{c.name}</div>
                                            <div className="text-xs text-slate-400 truncate">{c.subject}</div>
                                        </Link>
                                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-mono-alt uppercase ${statusColor(c.status)} whitespace-nowrap`}>
                                            {c.status}
                                        </span>
                                    </div>
                                    <div className="grid grid-cols-3 gap-2 mt-3 text-center">
                                        <div className="bg-slate-900/50 rounded-lg py-2">
                                            <div className="text-[10px] overline">Sent</div>
                                            <div className="text-sm font-mono-alt font-bold text-slate-100">{s.sent || 0}</div>
                                        </div>
                                        <div className="bg-slate-900/50 rounded-lg py-2">
                                            <div className="text-[10px] overline">Opens</div>
                                            <div className="text-sm font-mono-alt font-bold text-cyan-300">
                                                {s.opened || 0}{openRate && <span className="text-slate-500 text-[10px]"> ({openRate}%)</span>}
                                            </div>
                                        </div>
                                        <div className="bg-slate-900/50 rounded-lg py-2">
                                            <div className="text-[10px] overline">Clicks</div>
                                            <div className="text-sm font-mono-alt font-bold text-orange-300">{s.clicked || 0}</div>
                                        </div>
                                    </div>
                                    <div className="flex items-center justify-between mt-3 pt-3 border-t border-slate-800/60">
                                        <div className="text-[11px] text-slate-500 font-mono-alt">
                                            {c.created_at ? format(new Date(c.created_at), "MMM d, yyyy") : "—"}
                                        </div>
                                        <div className="flex gap-1">
                                            {(c.status === "scheduled" || c.status === "paused") && (
                                                <button
                                                    onClick={() => togglePause(c)}
                                                    className={`p-2 ${c.status === "paused" ? "text-emerald-300" : "text-amber-300"}`}
                                                    title={c.status === "paused" ? "Resume" : "Pause"}
                                                    data-testid={`campaign-card-${c.status === "paused" ? "resume" : "pause"}-${c.id}`}
                                                >
                                                    {c.status === "paused" ? <Play size={16} weight="fill" /> : <Pause size={16} weight="fill" />}
                                                </button>
                                            )}
                                            <Link to={`/admin/campaigns/${c.id}`} className="p-2 text-slate-400 hover:text-cyan-300">
                                                <Eye size={16} />
                                            </Link>
                                            <button onClick={() => remove(c.id)} className="p-2 text-slate-400 hover:text-red-400">
                                                <Trash size={16} />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </>
            )}
        </div>
    );
}
